// deep import on purpose: the package barrel pulls in React/MUI/emotion, which breaks
// this module's pure-util unit test (no DOM under --env=node)
import { coerceTdaValue } from "@dreebit-registry/vsm-microservice-components/dist/utils/coerceTdaValues";
import { TypeDefinitionAttribute } from "../types";
import getFilter from "./getFilter";
import { buildFilterCase, SearchAttributeFilter } from "./transformInstalledBaseAttributeFilters";

/**
 * Dynamically builds a SearchAssetFilter object from TDA attribute metadata.
 *
 * - Attributes with `dynamicFilterListKey` set → grouped as SearchAttributeFilter[]
 *   under the corresponding key (e.g., "installedBaseAttributeFilters").
 * - Attributes without `dynamicFilterListKey` → `attr.index` is used directly as
 *   the GraphQL filter key (requires backend to align TDA index with filter key).
 *
 * Values are cast to the type the filter input expects via `coerceTdaValue` — TDA
 * option values are always strings (schema: `optionValue.value: String!`), so a
 * `type: "bool"` select hands us "true"/"false" and the cast has to come from the
 * attribute metadata, not from the value.
 *
 * Note: Exclude the "search" attribute before calling (it belongs to the query, not the filter).
 */
export const buildDynamicListFilter = (
    filterValues: { [key: string]: any },
    attributes: TypeDefinitionAttribute[],
    staticFilters?: Record<string, any>
): Record<string, any> => {
    const result: Record<string, any> = {};
    const listGroups: Record<string, TypeDefinitionAttribute[]> = {};

    for (const attr of attributes) {
        if (attr.dynamicFilterListKey) {
            if (!listGroups[attr.dynamicFilterListKey]) {
                listGroups[attr.dynamicFilterListKey] = [];
            }
            listGroups[attr.dynamicFilterListKey].push(attr);
        } else {
            const value = filterValues[attr.index];
            if (value === undefined || value === null || value === "") continue;
            // Plain objects (e.g. date ranges {after, before}) are already
            // filter-shaped — pass through unchanged.
            const isPlainObject =
                typeof value === "object" && !Array.isArray(value);
            if (isPlainObject) {
                result[attr.index] = value;
                continue;
            }
            const comparator = Array.isArray(value) ? "in" : "equals";
            const filterValue = getFilter(coerceTdaValue(value, attr), comparator);
            if (filterValue !== null) {
                result[attr.index] = filterValue;
            }
        }
    }

    for (const [listKey, groupAttrs] of Object.entries(listGroups)) {
        const filters: SearchAttributeFilter[] = [];
        for (const attr of groupAttrs) {
            const value = filterValues[attr.index];
            if (value === undefined || value === null || value === ""
                || (Array.isArray(value) && value.length === 0)) continue;
            const filterCase = buildFilterCase(attr.type, coerceTdaValue(value, attr));
            if (filterCase) {
                filters.push({ attributeIndex: attr.index, filter: filterCase });
            }
        }
        if (filters.length > 0) {
            result[listKey] = filters;
        }
    }

    return { ...result, ...(staticFilters || {}) };
};
